import * as XLSX from 'xlsx';

export type SheetName =
  | 'location_types'
  | 'organization_types'
  | 'topic_types'
  | 'service_types'
  | 'locations'
  | 'organizations'
  | 'topics'
  | 'sections'
  | 'service_definitions'
  | 'users';

export const VALID_SHEET_NAMES: SheetName[] = [
  'location_types',
  'organization_types',
  'topic_types',
  'service_types',
  'locations',
  'organizations',
  'topics',
  'sections',
  'service_definitions',
  'users',
];

export type ParsedSheets = Partial<Record<SheetName, Record<string, string>[]>>;

export type ParseResult = {
  sheets: ParsedSheets;
  unknownSheets: string[];
  totalRows: number;
  errors: string[];
};

/**
 * Parse a multi-sheet XLSX file (from ArrayBuffer) into typed entity maps.
 * Sheet names are matched case-insensitively.
 */
export function parseXlsx(buffer: ArrayBuffer): ParseResult {
  const workbook = XLSX.read(buffer, { type: 'array' });

  const sheets: ParsedSheets = {};
  const unknownSheets: string[] = [];
  const errors: string[] = [];
  let totalRows = 0;

  for (const rawSheetName of workbook.SheetNames) {
    const normalized = rawSheetName.trim().toLowerCase() as SheetName;

    if (!VALID_SHEET_NAMES.includes(normalized)) {
      unknownSheets.push(rawSheetName);
      continue;
    }

    const worksheet = workbook.Sheets[rawSheetName];
    // Convert to array of objects (header row becomes keys)
    const rows = XLSX.utils.sheet_to_json<Record<string, any>>(worksheet, {
      defval: '',
      raw: false, // force all values to strings
    });

    // Normalize keys: trim whitespace
    const normalizedRows = rows.map((row) => {
      const cleaned: Record<string, string> = {};
      for (const [key, value] of Object.entries(row)) {
        cleaned[key.trim()] = String(value ?? '').trim();
      }
      return cleaned;
    });

    sheets[normalized] = normalizedRows;
    totalRows += normalizedRows.length;
  }

  if (totalRows === 0 && Object.keys(sheets).length === 0) {
    errors.push('No valid sheets found in workbook');
  }

  return { sheets, unknownSheets, totalRows, errors };
}



